<template>
  <Teleport to="body">
    <Transition name="flw-fade">
      <div v-if="showForceLogout" class="flw-overlay">
        <div class="flw-box" role="alertdialog" aria-live="assertive" aria-label="Sessão encerrada">
          <div v-if="!paused" class="flw-countdown">{{ forceLogoutSecondsLeft }}</div>
          <div class="flw-title">{{ TITLE[forceLogoutKind] }}</div>
          <div v-if="forceLogoutBy" class="flw-desc">Revogada por <strong>{{ forceLogoutBy }}</strong>.</div>
          <div v-if="forceLogoutKind === 'expired'" class="flw-desc">
            O acesso venceu enquanto a aba ficou parada. Digite a senha pra continuar de onde parou.
          </div>
          <div v-else-if="!paused" class="flw-desc">
            Salve o que estiver fazendo: você será desconectado em <strong>{{ forceLogoutSecondsLeft }}s</strong>.
          </div>

          <!-- Continuar logado: entra de novo com a senha, sem sair da tela (não vale pra conta desativada). -->
          <form v-if="canContinue" class="flw-continue" @submit.prevent="continueLogged">
            <label class="flw-label" for="flw-password">Senha de <strong>{{ email }}</strong></label>
            <input
              id="flw-password" v-model="password" class="flw-input" type="password" autocomplete="current-password"
              placeholder="Sua senha" :disabled="busy" @focus="pause" @input="pause"
            />
            <p v-if="error" class="flw-error">{{ error }}</p>
            <button type="submit" class="flw-btn flw-btn--primary" :disabled="busy || !password">
              {{ busy ? 'Entrando…' : 'Continuar logado' }}
            </button>
          </form>

          <div class="flw-actions">
            <button type="button" class="flw-btn" :class="canContinue ? 'flw-btn--ghost' : 'flw-btn--primary'" :disabled="busy" @click="handleLogout">
              Sair agora
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
// Aviso de sessão encerrada. "Continuar logado" faz um login novo com a senha e troca o token em todo o painel
// (adoptNewToken) — a tela, os rascunhos e o que estava aberto continuam. Pra conta desativada não tem como continuar.
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import {
  showForceLogout, forceLogoutSecondsLeft, forceLogoutKind, forceLogoutBy, logoutNowForced, resetForceLogout, pauseForceLogoutCountdown,
} from '@/composables/useForceLogout'
import { adoptNewToken } from '@/composables/useSessionTracking'
import { signInWootDash } from '@/services/auth'

const TITLE: Record<'account' | 'session' | 'expired', string> = {
  session: 'Esta sessão foi revogada pelo Sistema',
  expired: 'Sua sessão expirou',
  account: 'Você foi deslogado pelo Sistema',
}

const store  = useStore()
const router = useRouter()

const password = ref('')
const busy = ref(false)
const error = ref('')
const paused = ref(false)
const email = computed<string>(() => store.getters.authUser?.email ?? '')
const canContinue = computed(() => forceLogoutKind.value !== 'account' && !!email.value)

watch(showForceLogout, open => {
  if (!open) return
  password.value = ''
  error.value = ''
  paused.value = false
  // Expirou: não tem o que salvar com pressa — espera a senha.
  if (forceLogoutKind.value === 'expired') pause()
})

function pause() {
  if (paused.value) return
  paused.value = true
  pauseForceLogoutCountdown()
}

async function continueLogged() {
  if (!password.value || busy.value) return
  busy.value = true
  error.value = ''
  try {
    const session = await signInWootDash(email.value, password.value)
    adoptNewToken(store, session.token)
    password.value = ''
    resetForceLogout()
  } catch (e: any) {
    error.value = e?.message === 'Credenciais inválidas' ? 'Senha incorreta. Tente de novo.' : (e?.message || 'Não deu pra entrar. Tente de novo.')
  } finally {
    busy.value = false
  }
}

function handleLogout() {
  logoutNowForced(store, router)
}
</script>

<style scoped>
.flw-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, .55);
  display: flex;
  align-items: center;
  justify-content: center;
}

.flw-box {
  background: var(--white);
  border-radius: var(--r);
  padding: 32px;
  width: 340px;
  max-width: calc(100vw - 32px);
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .3);
}

.flw-countdown {
  font-size: 40px;
  font-weight: 800;
  color: var(--red);
  margin-bottom: 8px;
  font-variant-numeric: tabular-nums;
}

.flw-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink-80);
  margin-bottom: 6px;
}

.flw-desc {
  font-size: 13px;
  color: var(--ink-40);
  line-height: 1.5;
  margin-bottom: 20px;
}

.flw-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.flw-btn {
  padding: 10px 16px;
  border-radius: var(--r-sm);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: none;
  font-family: inherit;
}

.flw-btn--primary {
  background: var(--red);
  color: #fff;
}

.flw-btn--muted {
  background: transparent;
  color: var(--ink-40);
  border: 1px solid var(--ink-10);
}

.flw-fade-enter-active,
.flw-fade-leave-active {
  transition: opacity .15s ease;
}

.flw-fade-enter-from,
.flw-fade-leave-to {
  opacity: 0;
}

.flw-continue { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; text-align: left; }
.flw-label { font-size: 12px; color: var(--ink-40); }
.flw-label strong { color: var(--ink-80); }
.flw-input { padding: 10px 12px; border: 1.5px solid var(--ink-10); border-radius: var(--r-sm); font-size: 14px; font-family: inherit; color: var(--ink-80); background: var(--white); outline: none; }
.flw-input:focus { border-color: var(--red); }
.flw-error { margin: 0; font-size: 12px; color: #b91c1c; }
.flw-btn:disabled { opacity: .6; cursor: not-allowed; }
.flw-btn--ghost { background: transparent; color: var(--ink-40); border: 1.5px solid var(--ink-10); }
</style>
